import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { FeedBadge } from './FeedBadge';
import styles from './RecommendationFeedCard.module.css';

interface LinkedWager {
  readonly wagerId: string;
  readonly entityId: string;
  readonly entityName: string;
  readonly stake: number | null;
}

interface Props {
  readonly item: {
    readonly id: string;
    readonly adapterId: string;
    readonly track: string;
    readonly category: string;
    readonly statement: string;
    readonly context: string;
    readonly maturityDate: string;
    readonly suggestedStake: number;
    readonly suggestedDenomination: string;
    readonly linkedWagers: readonly LinkedWager[];
  };
}

const MAX_VISIBLE_WAGERS = 3;

const formatMaturity = (date: string): string => {
  const diff = new Date(date).getTime() - Date.now();
  if (diff < 0) return 'Expired';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const formatStake = (w: LinkedWager): string =>
  w.stake !== null ? `${w.entityName} bet ${w.stake} MC` : w.entityName;

const RecommendationFeedCard = ({ item }: Props): ReactElement => {
  const visible = item.linkedWagers.slice(0, MAX_VISIBLE_WAGERS);
  const overflow = item.linkedWagers.length - MAX_VISIBLE_WAGERS;

  return (
    <div className={styles.card} data-testid="rec-card">
      <FeedBadge type="recommendation" />
      <div className={styles.statement}>{item.statement}</div>
      <div className={styles.context}>{item.context}</div>
      <div className={styles.stake}>
        <span className={styles.stakeAmount}>{item.suggestedStake} MC</span>
        <span className={styles.stakeLabel}>suggested stake ({item.suggestedDenomination})</span>
      </div>
      {visible.length > 0 ? (
        <div className={styles.linkedWagers}>
          {visible.map((w) => (
            <Link key={w.wagerId} to={`/wager/${w.wagerId}`} className={styles.wagerLink} data-testid="rec-card-wager">
              {formatStake(w)}
            </Link>
          ))}
          {overflow > 0 ? (
            <span className={styles.overflowLabel}>+{overflow} more</span>
          ) : null}
        </div>
      ) : null}
      <div className={styles.meta}>
        <span>{item.track.replace('_', ' ')}</span>
        <span className={styles.sep}>&middot;</span>
        <span>{item.adapterId.replace(/[-_.]/g, ' ')}</span>
        <span className={styles.sep}>&middot;</span>
        <span>resolves {formatMaturity(item.maturityDate)}</span>
      </div>
    </div>
  );
};

export { RecommendationFeedCard };
